import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import Logo from '@/components/ui/Logo';
import Botao from '@/components/ui/Botao';
import Carregando from '@/components/ui/Carregando';
import type { RelatorioV2 } from '@/pages/gerenciar/painel/financeiro/relatorio/types';
import {
  dataBR,
  dataHoraBR,
  normalizarRelatorio,
} from '@/pages/gerenciar/painel/financeiro/relatorio/utils';
import Aparecer from '@/pages/gerenciar/painel/financeiro/relatorio/components/Aparecer';
import Principais from '@/pages/gerenciar/painel/financeiro/relatorio/components/Principais';
import OndeEstaCartao from '@/pages/gerenciar/painel/financeiro/relatorio/components/OndeEstaCartao';
import SecaoInscricoes from '@/pages/gerenciar/painel/financeiro/relatorio/components/SecaoInscricoes';
import SecaoAcao from '@/pages/gerenciar/painel/financeiro/relatorio/components/SecaoAcao';
import SecaoLoja from '@/pages/gerenciar/painel/financeiro/relatorio/components/SecaoLoja';
import SecaoDespesas from '@/pages/gerenciar/painel/financeiro/relatorio/components/SecaoDespesas';
import SecaoEntradas from '@/pages/gerenciar/painel/financeiro/relatorio/components/SecaoEntradas';
import './relatorio.css';

type Estado = 'carregando' | 'pronto' | 'erro';

export default function RelatorioFechamento() {
  const { eventoId } = useParams<{ eventoId: string }>();
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const idNum = Number(eventoId);

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [rel, setRel] = useState<RelatorioV2 | null>(null);
  const [nomeEvento, setNomeEvento] = useState('');
  const [inicio, setInicio] = useState<string | null>(null);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      navigate(
        `/entrar?voltar=${encodeURIComponent(`/gerenciar/${idNum}/relatorio-fechamento`)}`,
        { replace: true },
      );
      return;
    }
    let ativo = true;
    (async () => {
      setEstado('carregando');
      setErro(null);
      try {
        const { data, error } = await supabase.rpc('relatorio_fechamento_v2', { p_evento: idNum });
        if (error) throw error;

        // Nome e data do evento (só leitura; se falhar, seguimos sem eles).
        let nome = '';
        let ini: string | null = null;
        try {
          const { data: ev } = await supabase
            .from('eventos')
            .select('nome, inicio_em')
            .eq('id', idNum)
            .maybeSingle();
          const e = ev as { nome?: string; inicio_em?: string } | null;
          nome = e?.nome ?? '';
          ini = e?.inicio_em ?? null;
        } catch {
          /* segue sem os dados do evento */
        }

        if (!ativo) return;
        setRel(normalizarRelatorio(data));
        setNomeEvento(nome);
        setInicio(ini);
        setEstado('pronto');
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar o relatório.'));
          setEstado('erro');
        }
      }
    })();
    return () => {
      ativo = false;
    };
  }, [idNum, navigate, pronto, user]);

  if (estado === 'carregando') {
    return (
      <div className="pagina-relatorio min-h-screen bg-background-50 px-4 py-8">
        <div className="mx-auto w-full max-w-[880px]">
          <Carregando cartoes={6} />
        </div>
      </div>
    );
  }

  if (estado === 'erro' || !rel) {
    return (
      <div className="pagina-relatorio flex min-h-screen items-center justify-center bg-background-50 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-2xl border border-secondary-200 bg-white p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-danger-100 text-danger-600">
            <i className="ri-error-warning-line text-2xl" />
          </span>
          <h1 className="mt-4 text-xl font-bold text-foreground-950">Não foi possível abrir</h1>
          <p className="mt-2 text-sm text-foreground-600">{erro ?? 'Relatório indisponível.'}</p>
          <Botao
            variante="primario"
            cheio
            className="mt-6"
            onClick={() => navigate(`/gerenciar/${idNum}?aba=financeiro`)}
          >
            Voltar ao financeiro
          </Botao>
        </div>
      </div>
    );
  }

  return (
    <div className="pagina-relatorio min-h-screen bg-background-50">
      {/* Barra superior — fora da impressão */}
      <div className="no-print sticky top-0 z-40 border-b border-background-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[880px] items-center justify-between gap-3 px-4 py-3 md:px-6">
          <button
            type="button"
            onClick={() => navigate(`/gerenciar/${idNum}?aba=financeiro`)}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-sm font-medium text-secondary-600 transition-colors hover:text-foreground-900"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            Voltar
          </button>
          <Botao variante="primario" tamanho="md" icone="ri-printer-line" onClick={() => window.print()}>
            Salvar em PDF
          </Botao>
        </div>
      </div>

      <main className="mx-auto w-full max-w-[880px] px-4 py-6 md:py-8">
        {/* Cabeçalho do documento */}
        <header className="rel-secao rounded-2xl border border-background-200 bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,0.04)] md:p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Logo imgClassName="h-9 w-auto object-contain" />
              <h1 className="mt-2.5 text-[20px] font-extrabold leading-tight text-foreground-950 md:text-[22px]">
                Relatório do fechamento
              </h1>
              <p className="mt-0.5 text-[14px] font-semibold text-foreground-800">
                {nomeEvento || 'Evento'}
              </p>
              {inicio && <p className="text-[13px] text-foreground-500">Evento em {dataBR(inicio)}</p>}
            </div>
            <p className="shrink-0 text-right text-[12px] text-foreground-500">
              Gerado em
              <br />
              {dataHoraBR(rel.gerado_em)}
            </p>
          </div>
          {rel.frase && (
            <p className="mt-3 border-t border-background-100 pt-3 text-[13px] leading-relaxed text-foreground-600">
              {rel.frase}
            </p>
          )}
        </header>

        {/* 4 cartões — tudo o que fica acima da dobra */}
        <div className="mt-4">
          <Principais p={rel.principal} />
        </div>

        {/* Detalhes */}
        <div className="mt-4 space-y-4">
          {rel.onde_esta && <OndeEstaCartao o={rel.onde_esta} />}
          {rel.tem.inscricoes && rel.inscricoes && <SecaoInscricoes ins={rel.inscricoes} />}
          {rel.tem.acao_entre_amigos && <SecaoAcao itens={rel.acao_entre_amigos} />}
          {rel.tem.loja && rel.loja && <SecaoLoja loja={rel.loja} />}
          {rel.tem.despesas && <SecaoDespesas d={rel.despesas} />}
          {rel.tem.entradas_avulsas && rel.entradas_avulsas && (
            <SecaoEntradas e={rel.entradas_avulsas} />
          )}
        </div>

        {/* Conferência (impressão) */}
        <footer className="mt-8 border-t border-background-200 pt-6">
          <p className="text-[14px] text-foreground-800">
            Conferido por: ______________________________
          </p>
          <p className="mt-6 text-[14px] text-foreground-800">Data: ____ / ____ / ________</p>
        </footer>
      </main>
    </div>
  );
}